/**
 * One leader's profile page content: the SEO head built from the Contentful `leader` entry, then
 * `./Profile/LeadershipProfile.tsx` for the current language.
 *
 * Position: rendered only by `src/pages/about-us/leadership-team/[leaderSlug].tsx`, which passes
 * the entry in all three languages from `src/contentful/leader/getContentfulLeader.ts`.
 *
 * What it puts in `components/Shared/model/MetaHead.tsx`:
 * - title and description from the entry's `seoTitle` and `seoDescription`;
 * - `url` `/about-us/leadership-team/<slug>`, the canonical path, which must match the page route;
 * - the leader's photo as the share image, or `/images/RyersonLogo.png` (in `public/`) when the
 *   entry has no photo file;
 * - `company: false`, so no Organization JSON-LD. Instead, in English only, a schema.org `Person`
 *   block (name, job title, `worksFor` Ryerson). In French and Spanish the JSON-LD script is an
 *   empty object.
 *
 * The note above `MetaHead` in the render explains why these tags are absent from the exported
 * HTML; `components/NextLayouts/RootLayout.tsx` → `HasMounted` is the cause, and it applies to
 * every page.
 */
import React from "react";
import LeadershipProfile from "@components/Leadership/Profile/LeadershipProfile";
import { LanguageContent } from "@components/Shared/model/LanguageContent";
import { Language, useApplication } from "@ryerson/frontend.application";
import MetaHead, { MetaContent } from "@components/Shared/model/MetaHead";
import { ProcessedContentfulLeaderFieldsByLanguage } from "@contentful/leader/types";

interface LeaderContainerProps {
	/** The leader's profile in every language; the current language's entry is rendered. */
	contentfulLeaderByLanguage: ProcessedContentfulLeaderFieldsByLanguage;
}

/**
 * Renders the leader profile page's head and body for the current language. Only the current
 * language's slot of the meta object is filled; the other two stay empty strings and are never
 * read.
 */
const LeadershipProfileContainer: React.FC<LeaderContainerProps> = (props) => {
	const {
		localization: { language },
	} = useApplication();

	const contentfulLeaderForLanguage = props.contentfulLeaderByLanguage[language];

	let metaObj: LanguageContent<MetaContent> = {
		en: {
			title: "",
			description: "",
			url: "",
			company: false,
		},
		fr: {
			title: "",
			description: "",
			url: "",
			company: false,
		},
		es: {
			title: "",
			description: "",
			url: "",
			company: false,
		},
	};
	let structuredContent: any = {};
	let obj = {
		title: contentfulLeaderForLanguage.seoTitle,
		description: contentfulLeaderForLanguage.seoDescription,
		imageUrl:
			contentfulLeaderForLanguage.picture &&
			contentfulLeaderForLanguage.picture.file &&
			contentfulLeaderForLanguage.picture.file.url
				? contentfulLeaderForLanguage.picture.file.url
				: "/images/RyersonLogo.png",
		url: "/about-us/leadership-team/" + contentfulLeaderForLanguage.slug,
		company: false,
	};

	if (language === Language.en) {
		metaObj.en = obj;
		structuredContent = {
			"@context": "https://schema.org/",
			"@type": "Person",
			name: contentfulLeaderForLanguage.fullName,
			url: "/about-us/leadership-team/" + contentfulLeaderForLanguage.slug,
			image:
				contentfulLeaderForLanguage.picture &&
				contentfulLeaderForLanguage.picture.file &&
				contentfulLeaderForLanguage.picture.file.url
					? contentfulLeaderForLanguage.picture.file.url
					: "/images/RyersonLogo.png",
			jobTitle: contentfulLeaderForLanguage.title,
			worksFor: {
				"@type": "Organization",
				name: "Ryerson",
			},
			description: contentfulLeaderForLanguage.seoDescription,
		};
	} else if (language === Language.fr) {
		metaObj.fr = obj;
	} else {
		metaObj.es = obj;
	}

	return (
		<>
			{/* NOTE: the metaObj[language] will not work for SEO for following reasons:
				1. HasMounted blocks server render, so HTML (including meta tags) is empty → bad for SEO
				2. Localization comes from useState + useContext, which only works after hydration
				3. metaObj tags set via metaObj[language] rely on client-only state, so not seen by crawlers
				
				For SSG and SEO, we will need:
				1. Remove HasMounted and address the underlying issue
				2. Determine how to handle localization when deciding what crawlers should see in the meta tags
				3. Pass localization as a prop, not via useContext
				*/}
			<MetaHead content={metaObj[language]}>
				<script type="application/ld+json">{JSON.stringify(structuredContent)}</script>
			</MetaHead>
			<LeadershipProfile contentfulLeaderForLanguage={contentfulLeaderForLanguage} />
		</>
	);
};

export default LeadershipProfileContainer;
